export interface Country {
  name: string;
  dial_code: string;
  flag_position: string;
}
/**
 * Request body for assigning a ticket to a user.
 * This interface defines the structure of the request payload
 * when assigning a ticket to a user in the system.
 */
export interface  AssignTicketRequest {
  fname: string;
  lname: string;
  country: string;
  country_code: string;
  mobile: string;
  state: string;
  city: string;
  is_history?: string;
  assign_source?: string;
  language?: string;
  file_name_with_extension?: string;
  suggestion_image_url?: string;
}
/**
 * Request body for assigning a child ticket to a user.
 */
export interface AssignChildTicketRequest {
  fname: string;
  lname: string;
  file_name_with_extension: string;
}

export interface AssignTicketResponse {}

export interface TicketListResponse {
  message: string;
  data: TicketModel[];
}

// ticket.model.ts
export interface TicketModel {
  is_child: boolean;
  id: number;
  event_id: number;
  register_people_id: string;
  buyer_id: number;
  ticket_code: number;
  created_datetime: string;
  pass_code: string;
  badge_code: string | null;
  badge_created_datetime: string | null;
  badge_disconnect_datetime: string | null;

  event_register_summary_id: string;
  event_register_type_id: number;
  user_id: number;
  mobile: string;
  first_name: string;
  last_name: string;
  image_url: string;

  state: string;
  city: string;

  assigned_datetime: string | null;
  assign_source: string | null;
  paid_amount: number;
  assign_language: string | null;
  status: string;
  source: string;
  language: string;

  coupon_section: string | null;
  coupon_uuid: string | null;
  coupon_id: number | null;
  is_upgrade: boolean;
  transaction_id: string;

  mandatory_people_fields: string | null;
  app_display_name: string;
  coupon_name: string | null;

  media_url: string | null;
  bigthumb_url: string | null;
  smallthumb_url: string | null;

  ticket_type_code: string;
  ticket_type_name: string;
  badge_type: string;
  entry_type: string;
  for: string;

  is_invite: boolean;
  is_online: boolean;
  upgrade_ticket_type_ids: string | null;
  is_pre_register: boolean;

  ticket_primary_color: string;
  ticket_secondary_color: string;
  register_instructions: string;
  child_display_price: number;
  child_profile_default_image: string | null;
  ticket_note: string;

  ticket_icon_url: string;
  unassigned_user_logo: string;
  select_contact_logo: string;
  ticket_base_price: number;
  eventcode: string;

  buyer_mobile: string | null;
  buyer_first_name: string | null;
  buyer_last_name: string | null;
  buyer_image_url: string | null;
  buyer_image_bigthumb_url: string | null;
  buyer_image_smallthumb_url: string | null;
  buyer_organisation_name: string | null;
  buyer_email: string | null;

  invited_by_organisation_name: string | null;
  invited_by_brandname: string | null;
  invited_by_image_url: string | null;
  invited_by_image_bigthumb_url: string | null;
  invited_by_image_smallthumb_url: string | null;
  invited_by_org_userid: string | null;
  invitation_id: string | null;

  country: string;
  country_code: string;

  app_ticket_logo: string;
  is_farmer_first_flow_used: boolean;
  ticket_details_disclaimer: string | null;

  entry_expired_datetime: string | null;
  is_entry_expired: boolean;
  first_viewed_datetime: string | null;
  ticket_link: string | null;

  display_index: number;
  encoded_ticket_code: string;
  invite_friend: InviteFriend[];
  upgrade_register_types: UpgradeRegisterType[];
}

/** Share text in various languages */
export interface InviteFriend {
  lang: string; // 'English' | 'Hindi' | 'Marathi' | ...
  text: string; // URL-encoded lines (e.g., %0A)
}

/** Upgrade path details (possible targets) */
export interface UpgradeRegisterType {
  id: number;
  is_child: boolean;
  app_display_name: string;
  badge_type: string;
  base_price: number;
  charges_for_subscriber: number;
  code: string;
  currency: string;
  display_price: number;
  entry_type: string;
  event_id: number;
  for: string;
  gp_holders: string | null;
  email_name: string;
  is_invite: boolean;
  is_online: boolean;
  is_pre_register: boolean;
  max_ticket_for_user: number;
  name: string;
  sms_name: string;
  max_ticket_for_order: number;
  ticket_note: string | null;
  register_instructions: string | null;
  paid_amount: number;
  applicable_discount: ApplicableDiscount[];
  applied_total_discount: number;
}

/** Discounts applicable to an upgrade option */
export interface ApplicableDiscount {
  id: string; // UUID
  discount_amount: number;
  display_name: string;
  code: string;
}

/**
 * Represents the response containing the user's event ticket information and profile details.
 *
 * @property event_my_tickets - The user's tickets for the event.
 * @property user_profile - The profile information of the user.
 * @property user_profile.first_name - The user's first name.
 * @property user_profile.last_name - The user's last name.
 * @property user_profile.mobile - The user's mobile number.
 * @property user_profile.country - The user's country.
 * @property user_profile.country_code - The user's country code.
 * @property user_profile.state - The user's state.
 * @property user_profile.city - The user's city.
 * @property user_profile.email - The user's email address.
 * @property user_profile.user_id - The user's unique identifier.
 * @property user_profile.image_url - The URL of the user's profile image.
 * @property user_profile.category - The user's category.
 */
export interface UserEventStatusResp {
  event_my_tickets: EventMyTickets;
  user_profile: {
    first_name: string;
    last_name: string;
    mobile: string;
    country: string;
    country_code: string;
    state: string;
    city: string;
    email: string;
    user_id: number;
    image_url: string;
    category: string;
  };
}

/**
 * Represents a single ticket suggestion entry.
 */
export interface TicketSuggestion {
  first_name: string;
  last_name: string;
  mobile: string;
  state: string;
  city: string;
  image_url: string;
}

/**
 * Wrapper for API response of ticket suggestions.
 */
export interface TicketSuggestionResponse {
  message: string;
  data: TicketSuggestion[];
}

// Define an interface for clarity
export interface UserInfo {
  first_name: string;
  last_name: string;
  mobile: string;
  country: string;
  country_code: string;
  state: string;
  city: string;
  image_url: string;
  assign_source?: string;
  is_history?: number;
}
export interface GetImageUploadLinkDataModel {
  /** filename for the image being uploaded */
  title: string;
}

export interface S3Fields {
  acl?: string;
  "Content-Type"?: string;
  success_action_status?: string;
  key?: string;
  "x-amz-algorithm"?: string;
  "x-amz-credential"?: string;
  "x-amz-date"?: string;
  "x-amz-security-token"?: string;
  policy?: string;
  "x-amz-signature"?: string;
}

export interface S3MediaPost {
  url?: string;
  fields?: S3Fields;
}

export interface UploadData {
  ext?: string;
  file_name?: string;
  file_name_with_extension?: string;
  media_key?: string;
  original_key?: string;
  media_url?: string;
  original_media_url?: string;
  s3_media_post?: S3MediaPost;
}

/**
 * Represents the ticket-related information for a user's participation in an event.
 *
 * @property tickets_bought_by_me - Total number of tickets bought by the user.
 * @property assigned_tickets_bought_by_me - Number of tickets bought by the user that have been assigned.
 * @property unassigned_tickets_bought_by_me - Number of tickets bought by the user that are unassigned.
 * @property tickets_assigned_for_me - Number of tickets assigned to the user by others.
 * @property event_pending_invitation_count - Number of pending event invitations for the user.
 * @property registrationfield_empty_ticket_count - Number of tickets with empty registration fields.
 * @property registrationfield_empty_first_ticket_encoded_code - Encoded code of the first ticket with empty registration fields, or null if none.
 * @property my_valid_ticket_count - Number of valid tickets owned by the user.
 * @property event_attendee_details - Details about the user's attendance at the event.
 *   @property event_attendee_details.user_id - Unique identifier of the user.
 *   @property event_attendee_details.event_id - Unique identifier of the event.
 *   @property event_attendee_details.register_date - Date when the user registered for the event.
 *   @property event_attendee_details.in_time - Time when the user checked in, or null if not checked in.
 *   @property event_attendee_details.is_join - Indicates if the user has joined the event, or null if unknown.
 *   @property event_attendee_details.status - Status code representing the user's attendance.
 *   @property event_attendee_details.out_time - Time when the user checked out, or null if not checked out.
 *   @property event_attendee_details.transaction_payment_id - Payment transaction ID, or null if not applicable.
 *   @property event_attendee_details.whatsapp_reminder_sent_datetime - Datetime when WhatsApp reminder was sent, or null.
 *   @property event_attendee_details.whatsapp_eventvideo_sent_datetime - Datetime when WhatsApp event video was sent, or null.
 *   @property event_attendee_details.whatsapp_rescheduled_message_sent_datetime - Datetime when WhatsApp rescheduled message was sent, or null.
 *   @property event_attendee_details.source - Source of the attendee registration.
 *   @property event_attendee_details.is_ground_attendee - Indicates if the user is a ground attendee.
 *   @property event_attendee_details.is_business_visitor - Indicates if the user is a business visitor.
 */
export interface EventMyTickets {
  tickets_bought_by_me: number;
  assigned_tickets_bought_by_me: number;
  unassigned_tickets_bought_by_me: number;
  tickets_assigned_for_me: number;
  event_pending_invitation_count: number;
  registrationfield_empty_ticket_count: number;
  registrationfield_empty_first_ticket_encoded_code: string | null;
  my_valid_ticket_count: number;
  event_attendee_details: {
    user_id: number;
    event_id: number;
    register_date: string;
    in_time: string | null;
    is_join: boolean | null;
    status: number;
    out_time: string | null;
    transaction_payment_id: string | null;
    whatsapp_reminder_sent_datetime: string | null;
    whatsapp_eventvideo_sent_datetime: string | null;
    whatsapp_rescheduled_message_sent_datetime: string | null;
    source: string;
    is_ground_attendee: boolean;
    is_business_visitor: boolean;
  };
}

export const AssignTicketIconModel = {
  multi_entry: {
    white: "assets/images/all_day_pass_white.png",
    primary: "#895700",
    secondary: "#FFF6E6",
  },
  biz_pass: {
    white: "assets/images/business_white.png",
    primary: "#0063CE",
    secondary: "#E6F2FF",
  },
  child_single_entry: {
    white: "assets/images/child_white.png",
    primary: "#D9A900",
    secondary: "#FFFADD",
  },
  first_day_single_entry: {
    white: "assets/images/first_day_white.png",
    primary: "#EF8A04",
    secondary: "#FFF7E4",
  },
  single_entry: {
    white: "assets/images/single_entry_white.png",
    primary: "#008940",
    secondary: "#EFFFF7",
  },
};

export interface InitiateUpgradePaymentResModel {
  amount: number; // in paise (e.g., 40000 = ₹400)
  amount_due: number; // paise
  amount_paid: number; // paise
  attempts: number;
  created_at: number; // epoch seconds
  currency: string;
  entity: string;
  id: string; // Razorpay order id (same as razorpay_order_id)
  notes: RazorpayUpgardeNotes;
  offer_id: string | null;
  receipt: string;
  status: string;

  // App/business context
  payment_for: string;
  payment_for_item_id: number;
  payment_for_item_uuid: string;

  // Convenience duplicates / keys
  razorpay_order_id: string;
  summary_id: string;
  razorpay_key: string;

  // Payer / event context
  mobile: string;
  first_name: string;
  last_name: string;
  email: string;

  eventcode: string;
  event_name: string;
  email_name: string;
  event_dashboard_logo_color: string;
  event_primary_color: string;
}
export interface RazorpayUpgardeNotes {
  payment_for?: string;
  [key: string]: unknown;
}

/** Request body to initiate an upgrade payment */
export interface RazorpayUpgradeRequest {
  /** New ticket type to upgrade into */
  new_register_type_code: string;
  language: string;
  source: string;
}

// Request body to verify an upgrade payment
export interface VerifyUpgradePaymentRequest {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
  summary_id: string;
  language: string;
  source: string;
}

export const PHOTO_REQUIRED_TICKET_TYPES = new Set([
  'biz_pass',
  'multi_entry'
]);


export const LANG_MAP: Record<string, string> = {
  en: 'English',
  hi: 'Hindi',
  mr: 'Marathi'
};

export interface UpgradeTicketMediaPayload {
  file_name_with_extension: string;
}

export interface UpgradeTicketMediaResponse {
  message: string;
}